import { isSafeHostname } from '@/lib/apUtils'
import type { Post } from '@/types/posts'

/**
 * Build a shareable URL for a post.
 *
 * - AT Protocol posts: permanent Mackinac permalink using the author DID and rkey
 * - Mastodon posts: original HTML URL from the API, falling back to the ActivityPub URI
 *
 * Returns null if the URL cannot be constructed or is unsafe.
 */
export function buildShareUrl(post: Post): string | null {
    if (!post.isActivityPub) {
        // AT Protocol: use DID for a permanent link (handles can change)
        const did = post.author?.did
        const rkey = post.rkey
        if (!did || !rkey) return null
        return `https://app.mackinac.social/profile/${did}/post/${rkey}`
    }

    // Mastodon / ActivityPub: use HTML URL if available, fall back to ActivityPub URI
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty url should fall through to uri, not stay blank
    const candidate = post.url || post.uri || null
    if (!candidate) return null

    // Validate the URL from the ActivityPub source before sharing
    try {
        const parsed = new URL(candidate)
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
        if (!isSafeHostname(parsed.hostname)) return null
    } catch {
        return null
    }

    return candidate
}

/**
 * Build a shareable Mackinac URL for an AT Protocol feed generator.
 *
 * Feed URL format: https://app.mackinac.social/profile/{creator-handle}/feed/{rkey}
 *
 * Returns null if the URI or creator handle is missing/malformed.
 */
export function buildFeedShareUrl(feed: {
    uri: string
    creator?: { handle?: string }
}): string | null {
    const handle = feed.creator?.handle
    if (!handle) return null

    // URI format: at://did:plc:xxx/app.bsky.feed.generator/rkey
    const rkey = feed.uri.split('/').pop()
    if (!rkey) return null

    return `https://app.mackinac.social/profile/${handle}/feed/${rkey}`
}

/**
 * Build a shareable Mackinac URL for an article.
 *
 * URL format: https://app.mackinac.social/profile/{did}/article/{rkey}
 *
 * Returns null if did or rkey is missing.
 */
export function buildArticleShareUrl(article: { did: string; rkey: string }): string | null {
    if (!article.did || !article.rkey) return null
    return `https://app.mackinac.social/profile/${article.did}/article/${article.rkey}`
}
